import { useEffect, useState } from "react";
import toastr from "toastr";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  LogIn,
  Loader2,
  Store as StoreIcon,
  Filter,
  Search,
  RotateCcw,
  X,
  MapPin,
  Mail,
  Phone,
  User,
  CalendarDays,
  AlertCircle,
  QrCode,
} from "lucide-react";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import { fetchStoresList } from "../../store/storeSlices/storeSlice";
import Pagination from "../../hooks/Pagination";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import { storeAdminLogin, isApiSuccess } from "../../services/api";
import StoreQrCard from "../StoreQrCard"; 
import { toggleStoreStatus } from "../../store/storeSlices/storeStatusSlice"; // adjust path

const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

const BADGE = {
  store: "bg-blue-50 text-blue-700 border border-blue-200",
  event: "bg-purple-50 text-purple-700 border border-purple-200",
  enquiry: "bg-amber-50 text-amber-700 border border-amber-200",
  payment: "bg-green-50 text-green-700 border border-green-200",
};

const inputCls = "w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none";

const parseJson = (value, fallback) => {
  if (typeof value !== "string") return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export default function Store() {
  useDocumentTitle("Welcome to Admin | Store");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginStoreId, setLoginStoreId] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [qrStore, setQrStore] = useState(null); // store whose QR is open in the modal
  const [statusStore, setStatusStore] = useState(null); // store waiting for the active/inactive confirmation
  const togglingId = useSelector((state) => state.storeStatus.togglingId);

  const { list: storelist, loading, currentPage, totalPages, error } = useSelector((state) => state.store);
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Filters (hidden by default, open on click of the Filter button)
  const [showFilters, setShowFilters] = useState(false);
  const [keywordDraft, setKeywordDraft] = useState("");
  const [keyword, setKeyword] = useState(""); // applied keyword

  useEffect(() => {
    dispatch(fetchStoresList({ perPage, page, keyword: keyword || null }));
  }, [dispatch, page, keyword]);

  const handleApply = (e) => {
    e.preventDefault();
    setPage(1);
    setKeyword(keywordDraft.trim());
  };

  const handleReset = () => {
    setKeywordDraft("");
    setKeyword("");
    setPage(1);
  };

  const activeFilters = keyword ? 1 : 0;

  const handleStoreLogin = async (item) => {
    setLoginError("");
    if (!item?.email) {
      setLoginError("This store has no email, so login is not possible.");
      return;
    }

    // Open the tab synchronously inside the click so popup blockers allow it
    const tab = window.open("", "_blank");
    if (!tab) {
      setLoginError("Popup blocked. Please allow popups for this site and try again.");
      return;
    }
    tab.document.title = "Logging in...";

    setLoginStoreId(item.id);
    try {
      let admin = 1;
      const res = await storeAdminLogin(item.email, admin);
      if (!isApiSuccess(res) || !res?.access_token) throw res;

      localStorage.setItem(
        "store_panel_auth",
        JSON.stringify({
          token: res.access_token,
          tokenType: res.token_type,
          store: res.store,
          loggedInAt: Date.now(),
        })
      );
      tab.location.href = "/store-panel/";
    } catch (err) {
      tab.close();
      setLoginError(err?.message || "Store login failed. Please try again.");
    } finally {
      setLoginStoreId(null);
    }
  };

  const setCurrentPage = (p) => setPage(p);

  const openDaysSummary = (item) => {
    if (!item?.store_timings) return "";
    const timings = parseJson(item.store_timings, null);
    if (!timings || typeof timings !== "object") return "";
    const openDays = Object.entries(timings)
      .filter(([, v]) => v?.is_open)
      .map(([day]) => DAY_LABELS[day] || day);
    return openDays.length ? openDays.join(", ") : "Closed all week";
  };

  const handleEdit = (item) => {
    localStorage.removeItem("storeEditData");
    localStorage.setItem("storeEditData", JSON.stringify(item));
    navigate("/store/edit");
  };

  const isStoreActive = (item) => item?.is_active == null || Number(item.is_active) === 1;

  const confirmStatusChange = async () => {
    if (!statusStore) return;
    const res = await dispatch(toggleStoreStatus(statusStore.id));
    if (toggleStoreStatus.fulfilled.match(res)) {
      toastr.success(res.payload?.message || "Status updated.");
      setStatusStore(null);
      dispatch(fetchStoresList({ perPage, page, keyword: keyword || null })); // refresh the list
    } else {
      toastr.error(res.payload || "Unable to change the store status.");
    }
  };

  const errorMessage = error || loginError;

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="rounded-xl p-4 text-white shadow-lg bg-gradient-to-r from-[#f4a406] to-[#d89005]">
        <div className="flex items-center gap-2">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
            <StoreIcon size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold leading-tight">Store / Event List</h2>
            <p className="text-sm text-white/90">
              {keyword ? `Showing results for “${keyword}”` : "Manage your stores and events"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            title="Filter"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
          >
            <Filter size={16} />
            {showFilters ? "Hide filters" : "Filters"}
            {activeFilters > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#d89005] text-xs font-bold inline-flex items-center justify-center">
                {activeFilters}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters (collapsed by default) */}
      {showFilters && (
        <form onSubmit={handleApply} className="bg-white shadow-lg rounded-2xl p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  placeholder="Store name, address, email, phone or contact name"
                  autoComplete="off"
                  className={`${inputCls} pl-9 pr-9`}
                />
                {keywordDraft && (
                  <button
                    type="button"
                    onClick={() => setKeywordDraft("")}
                    title="Clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-1 py-2 px-4 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors text-sm"
              >
                <Search size={16} /> Search
              </button>
              <button
                type="button"
                onClick={handleReset}
                title="Reset filters"
                className="py-2 px-3 border rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <RotateCcw size={16} />
              </button>
            </div>
          </div>
        </form>
      )}

      {errorMessage && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle size={16} className="shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* List */}
      <div className="bg-white shadow-lg rounded-2xl p-2">
        <div className="overflow-auto border rounded-xl">
          <table className="w-full min-w-[950px] text-sm text-left">
            <thead className="text-gray-700 bg-gray-100">
              <tr>
                <th className="p-2">Store</th>
                <th className="p-2">Contact</th>
                <th className="p-2">Type</th>
                <th className="p-2">City</th>
                {/* <th className="p-3">Product types</th> */}
                <th className="p-2">Mode</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td className="p-2" colSpan={7}>
                    <ShimmerLoader />
                    <ShimmerLoader />
                    <ShimmerLoader />
                  </td>
                </tr>
              ) : storelist.length > 0 ? (
                storelist.map((item) => {
                  const isStore = item.vendortype == 0;
                  const types = parseJson(item?.product_types, []);
                  const typeList = Array.isArray(types) ? types : [];
                  const days = openDaysSummary(item);
                  const initial = (item?.store_name || "?").trim().charAt(0).toUpperCase();
                  const active = isStoreActive(item);

                  return (
                    <tr key={item.id} className="hover:bg-gray-50 align-top">
                      <td className="p-2 border-l-4 border-[#f4a406]">
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 w-8 h-8 rounded-xl bg-amber-50 text-[#d89005] font-bold flex items-center justify-center">
                            {initial}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{item?.store_name || "-"}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              ID #{item?.id}
                            </div>
                            {/* {days && (
                              <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                                <CalendarDays size={12} /> {days}
                              </div>
                            )} */}
                          </div>
                        </div>
                      </td>
                      <td className="p-2">
                        {item?.contact_name && (
                          <div className="font-medium inline-flex items-center gap-1">
                            <User size={13} className="text-gray-400" /> {item.contact_name}
                          </div>
                        )}
                        {item?.email && (
                          <div className="text-xs text-gray-600 break-all inline-flex items-center gap-1 mt-1 w-full">
                            <Mail size={12} className="text-gray-400 shrink-0" /> {item.email}
                          </div>
                        )}
                        {item?.phone && (
                          <div className="text-xs text-gray-600 inline-flex items-center gap-1 mt-1 w-full">
                            <Phone size={12} className="text-gray-400 shrink-0" /> {item.phone}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${isStore ? BADGE.store : BADGE.event}`}>
                          {isStore ? "Store" : "Event"}
                        </span>
                      </td>
                      <td className="p-2">
                        {item?.fullRegionName ? (
                          <span className="inline-flex items-start gap-1 text-gray-700">
                            <MapPin size={14} className="text-gray-400 mt-0.5 shrink-0" /> {item.fullRegionName}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      {/* <td className="p-3">
                        {typeList.length ? (
                          <div className="flex flex-wrap gap-1">
                            {typeList.map((t, i) => (
                              <span key={`${t}-${i}`} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td> */}
                      <td className="p-2">
                        <div className="flex flex-wrap gap-1">
                          {item?.enquiry_enabled || !item?.payment_enabled ? (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${BADGE.enquiry}`}>Enquiry</span>
                          ) : null}
                          {item?.payment_enabled ? (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${BADGE.payment}`}>Payment</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={active}
                            aria-label={active ? "Deactivate store" : "Activate store"}
                            title={active ? "Active - click to deactivate" : "Inactive - click to activate"}
                            disabled={togglingId === item.id}
                            onClick={() => setStatusStore(item)}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f4a406] disabled:opacity-50 ${active ? "bg-green-500" : "bg-gray-300"}`}
                          >
                            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${active ? "translate-x-5" : "translate-x-0.5"}`} />
                          </button>
                          <span className={`text-xs font-medium ${active ? "text-green-700" : "text-gray-500"}`}>
                            {active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="w-9 h-9 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 inline-flex items-center justify-center transition-colors disabled:opacity-50"
                            title="Login as store"
                            disabled={loginStoreId === item.id}
                            onClick={() => handleStoreLogin(item)}
                          >
                            {loginStoreId === item.id ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                          </button>
                          <button
                            className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 inline-flex items-center justify-center transition-colors"
                            title="View / download QR"
                            onClick={() => setQrStore(item)}
                          >
                            <QrCode size={16} />
                          </button>
                          <button
                            className="w-9 h-9 rounded-xl bg-amber-50 text-[#f4a406] hover:bg-amber-100 inline-flex items-center justify-center transition-colors"
                            title="Edit store"
                            onClick={() => handleEdit(item)}
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="p-8 text-center text-gray-500" colSpan={7}>
                    No record found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </div>
      </div>

      {/* Active / inactive confirmation */}
      {statusStore && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => togglingId == null && setStatusStore(null)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900">
              {isStoreActive(statusStore) ? "Make this store inactive?" : "Make this store active?"}
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              {isStoreActive(statusStore)
                ? `Are you sure? "${statusStore.store_name}" will no longer show in store search, and customers can't pay it by QR.`
                : `Are you sure? "${statusStore.store_name}" will show in store search again and accept QR payments.`}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStatusStore(null)}
                disabled={togglingId != null}
                className="rounded-xl border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmStatusChange}
                disabled={togglingId != null}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-60 ${isStoreActive(statusStore) ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}
              >
                {togglingId != null && <Loader2 size={14} className="animate-spin" />}
                {isStoreActive(statusStore) ? "Yes, make inactive" : "Yes, make active"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store QR modal */}
      {qrStore && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setQrStore(null)}
        >
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setQrStore(null)}
              title="Close"
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 print:hidden"
            >
              <X size={18} />
            </button>
            <StoreQrCard storeName={qrStore.store_name} handle={qrStore.qr_handle} />
          </div>
        </div>
      )}
    </div>
  );
}